import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/client/lib/api";
import { useAuth } from "@/client/lib/auth";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { CoordinatorenDialog } from "@/client/components/admin/CoordinatorenDialog";
import { UsersIcon, EyeIcon, PencilIcon } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { Badge } from "@/client/components/ui/badge";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogPanel,
  DialogFooter,
} from "@/client/components/ui/dialog";
import { SchoolAanleveringView } from "./SubmissionPage";

// Beheerder-only editor for a school's vestigingsnummer (external_code). The server is the real
// authority (admin-role gate on PUT /:id/vestigingsnummer); this just provides the UI.
function VestigingsnummerDialog({
  schoolId,
  current,
  open,
  onClose,
  onSaved,
}: {
  schoolId: number;
  current: string | null;
  open: boolean;
  onClose: () => void;
  onSaved: (code: string | null) => void;
}) {
  const [value, setValue] = useState(current ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setValue(current ?? ""); setError(null); }
  }, [open, current]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await (api.api.admin.schools as any)[":id"].vestigingsnummer.$put({
        param: { id: String(schoolId) },
        json: { externalCode: value },
      });
      if (res.ok) {
        const data = await res.json();
        onSaved(data.externalCode ?? null);
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message ?? "Opslaan is niet gelukt. Probeer het opnieuw.");
      }
    } catch {
      setError("Opslaan is niet gelukt. Probeer het opnieuw.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPopup className="w-[92vw] max-w-sm">
        <DialogHeader>
          <DialogTitle>Vestigingsnummer</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-2">
            <Label htmlFor="vestigingsnummer">Vestigingsnummer (BRIN)</Label>
            <Input
              id="vestigingsnummer"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="24ZB00"
              className="font-mono uppercase"
              autoFocus
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Zes tekens, bijvoorbeeld 24ZB00. Laat het veld leeg om het nummer te wissen.
            </p>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuleren</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Opslaan..." : "Opslaan"}</Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

export function AdminSchoolDetailPage() {
  const { seg } = useParams<{ seg: string }>();
  const { user } = useAuth();
  const isAdmin = !!user?.roles.includes("admin");
  const schoolId = Number(seg);
  const [school, setSchool] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Whether the school has any submitted version (reported by the aanlevering view once it loads).
  const [hasSubmitted, setHasSubmitted] = useState<boolean | null>(null);
  const [coordOpen, setCoordOpen] = useState(false);
  const [vestOpen, setVestOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!seg) return;
        const res = await (api.api.admin.schools as any)[":id"].$get({ param: { id: seg } });
        if (res.ok) {
          const data = await res.json();
          setSchool(data.school);
        }
      } catch {}
      setLoading(false);
    })();
  }, [seg]);

  if (loading) return <p className="text-muted-foreground">School laden...</p>;
  if (!school) return <p className="text-muted-foreground">School niet gevonden.</p>;

  // Compact metadata line in the breadcrumb subtitle: korte naam, denominatie, vestigingsnummer
  // (with a beheerder-only edit pencil) and an "Inactief" flag when the school is archived.
  const parts: ReactNode[] = [];
  if (school.shortName) parts.push(<span key="kn">Korte naam: {school.shortName}</span>);
  if (school.denominationCode) parts.push(<span key="den">{school.denominationCode}</span>);
  parts.push(
    <span key="vest" className="inline-flex items-center gap-1">
      Vestigingsnr.:{" "}
      {school.externalCode ? (
        <span className="font-mono text-foreground">{school.externalCode}</span>
      ) : (
        <span className="italic">niet ingesteld</span>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={() => setVestOpen(true)}
          aria-label="Vestigingsnummer bewerken"
          title="Vestigingsnummer bewerken"
          className="cursor-pointer rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <PencilIcon className="size-3" />
        </button>
      )}
    </span>,
  );
  if (!school.isActive) parts.push(<span key="inact" className="text-amber-700">Inactief</span>);

  const subtitle = (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-muted-foreground/50">·</span>}
          {p}
        </span>
      ))}
    </span>
  );

  return (
    <div className="space-y-5">
      <AdminPageHeader
        items={[{ label: "Scholen", to: "/admin/scholen" }, { label: school.officialName }]}
        subtitle={subtitle}
        action={
          <div className="flex items-center gap-3">
            {hasSubmitted ? (
              <Badge variant="outline" size="sm" className="border-emerald-300 text-emerald-700">Neemt deel</Badge>
            ) : hasSubmitted === false ? (
              <Badge variant="outline" size="sm" className="border-amber-300 text-amber-700">Nog niets aangeleverd</Badge>
            ) : null}
            <Button size="sm" variant="outline" onClick={() => setCoordOpen(true)}>
              <UsersIcon /> Coördinatoren
            </Button>
            {/* Read-only preview of the school's own portal, in a new tab. Only shown to staff who
                have been granted preview access (the server enforces read-only regardless). */}
            {user?.canPreviewSchools && (
              <Button
                size="sm"
                variant="outline"
                render={<a href={`/school/${schoolId}/overzicht`} target="_blank" rel="noopener noreferrer" />}
              >
                <EyeIcon /> Bekijk schoolportaal
              </Button>
            )}
          </div>
        }
      />

      <SchoolAanleveringView schoolId={schoolId} onSubmissionState={setHasSubmitted} />

      <CoordinatorenDialog
        schoolId={schoolId}
        schoolName={school.officialName}
        open={coordOpen}
        onClose={() => setCoordOpen(false)}
      />

      {isAdmin && (
        <VestigingsnummerDialog
          schoolId={schoolId}
          current={school.externalCode ?? null}
          open={vestOpen}
          onClose={() => setVestOpen(false)}
          onSaved={(code) => setSchool((s: any) => ({ ...s, externalCode: code }))}
        />
      )}
    </div>
  );
}
